import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import type { Race } from '../../types';
import { useUserRaces } from '../../context/UserRacesContext';

interface RaceMarkerClusterProps {
  races: Race[];
  selectedRace: Race | null;
  onRaceSelect: (race: Race | null) => void;
  onRaceHover: (race: Race | null) => void;
  hoveredRaceId?: string | null;
}

const STAR_OVERLAY_HTML =
  '<span class="race-marker-star" aria-hidden="true">★</span>';

function createMarkerIcon(
  race: Race,
  isSelected: boolean,
  isHovered: boolean,
  isBookmarked: boolean,
): L.DivIcon {
  const classes = [
    'race-marker-dot',
    `status-${race.status}`,
    race.status === 'open' ? 'pulse' : '',
    isSelected ? 'selected' : '',
    isHovered ? 'hovered' : '',
    isBookmarked ? 'bookmarked' : '',
  ].filter(Boolean).join(' ');

  return L.divIcon({
    className: 'race-marker',
    html: `<div class="${classes}"></div>${isBookmarked ? STAR_OVERLAY_HTML : ''}`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  let sizeClass = 'marker-cluster-small';
  let size = 36;
  if (count >= 50) {
    sizeClass = 'marker-cluster-large';
    size = 48;
  } else if (count >= 10) {
    sizeClass = 'marker-cluster-medium';
    size = 42;
  }

  return L.divIcon({
    html: `<div><span>${count}</span></div>`,
    className: `marker-cluster-custom ${sizeClass}`,
    iconSize: L.point(size, size),
  });
}

export function RaceMarkerCluster({
  races,
  selectedRace,
  onRaceSelect,
  onRaceHover,
  hoveredRaceId,
}: RaceMarkerClusterProps) {
  const map = useMap();
  const { bookmarkedIds } = useUserRaces();
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const markerMapRef = useRef<Map<string, L.Marker>>(new Map());
  const raceMapRef = useRef<Map<string, Race>>(new Map());

  // Keep callback refs stable
  const onRaceSelectRef = useRef(onRaceSelect);
  const onRaceHoverRef = useRef(onRaceHover);
  const selectedRaceRef = useRef(selectedRace);
  const bookmarkedIdsRef = useRef<Set<string>>(bookmarkedIds);
  useEffect(() => {
    onRaceSelectRef.current = onRaceSelect;
    onRaceHoverRef.current = onRaceHover;
    selectedRaceRef.current = selectedRace;
    bookmarkedIdsRef.current = bookmarkedIds;
  }, [onRaceSelect, onRaceHover, selectedRace, bookmarkedIds]);

  // Initialize cluster group
  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      animate: true,
    });

    clusterGroupRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    return () => {
      map.removeLayer(clusterGroup);
      clusterGroupRef.current = null;
      markerMapRef.current = new Map();
      raceMapRef.current = new Map();
    };
  }, [map]);

  // Update markers when races or selection change (NOT hoveredRaceId)
  useEffect(() => {
    const clusterGroup = clusterGroupRef.current;
    if (!clusterGroup) return;

    const oldMarkerMap = markerMapRef.current;
    const newMarkerMap = new Map<string, L.Marker>();
    const newRaceMap = new Map<string, Race>();
    const raceIds = new Set(races.map((r) => r.id));

    // Remove markers for races no longer present
    oldMarkerMap.forEach((marker, id) => {
      if (!raceIds.has(id)) {
        clusterGroup.removeLayer(marker);
      }
    });

    // Add or update markers
    races.forEach((race) => {
      newRaceMap.set(race.id, race);
      const existing = oldMarkerMap.get(race.id);

      if (existing) {
        // Update position if changed
        const pos = existing.getLatLng();
        if (pos.lat !== race.lat || pos.lng !== race.lng) {
          existing.setLatLng([race.lat, race.lng]);
        }
        // Update icon
        const isSelected = selectedRace?.id === race.id;
        existing.setIcon(createMarkerIcon(race, isSelected, false, bookmarkedIdsRef.current.has(race.id)));
        newMarkerMap.set(race.id, existing);
      } else {
        // Create new marker
        const isSelected = selectedRace?.id === race.id;
        const marker = L.marker(
          [race.lat, race.lng],
          { icon: createMarkerIcon(race, isSelected, false, bookmarkedIdsRef.current.has(race.id)) }
        );

        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const r = raceMapRef.current.get(race.id);
          if (r) {
            const isCurrentlySelected = selectedRaceRef.current?.id === r.id;
            onRaceSelectRef.current(isCurrentlySelected ? null : r);
          }
        });

        marker.on('mouseover', () => {
          const r = raceMapRef.current.get(race.id);
          if (r) onRaceHoverRef.current(r);
        });

        marker.on('mouseout', () => {
          onRaceHoverRef.current(null);
        });

        clusterGroup.addLayer(marker);
        newMarkerMap.set(race.id, marker);
      }
    });

    markerMapRef.current = newMarkerMap;
    raceMapRef.current = newRaceMap;
  }, [races, selectedRace]);

  // Refresh marker icons when bookmarked set changes (adds/removes star overlay).
  // At ~175 races total the full-pass is inexpensive; toggles touch all markers.
  useEffect(() => {
    const markerMap = markerMapRef.current;
    const raceMap = raceMapRef.current;
    markerMap.forEach((marker, id) => {
      const race = raceMap.get(id);
      if (!race) return;
      const isSelected = selectedRace?.id === id;
      marker.setIcon(createMarkerIcon(race, isSelected, false, bookmarkedIds.has(id)));
    });
  }, [bookmarkedIds, selectedRace]);

  // Update only hovered marker icons (max 2 markers touched per change)
  const prevHoveredIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevId = prevHoveredIdRef.current;
    const currId = hoveredRaceId ?? null;
    prevHoveredIdRef.current = currId;

    if (prevId === currId) return;

    const markerMap = markerMapRef.current;
    const raceMap = raceMapRef.current;

    // Restore previous hovered marker to normal
    if (prevId) {
      const marker = markerMap.get(prevId);
      const race = raceMap.get(prevId);
      if (marker && race) {
        const isSelected = selectedRace?.id === prevId;
        marker.setIcon(createMarkerIcon(race, isSelected, false, bookmarkedIdsRef.current.has(race.id)));
      }
    }

    // Set current hovered marker
    if (currId) {
      const marker = markerMap.get(currId);
      const race = raceMap.get(currId);
      if (marker && race) {
        const isSelected = selectedRace?.id === currId;
        marker.setIcon(createMarkerIcon(race, isSelected, true, bookmarkedIdsRef.current.has(race.id)));
      }
    }
  }, [hoveredRaceId, selectedRace]);

  // Fly to selected race
  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedRace && selectedRace.id !== prevSelectedRef.current) {
      map.flyTo([selectedRace.lat, selectedRace.lng], Math.max(map.getZoom(), 10), {
        duration: 0.8,
      });
    }
    prevSelectedRef.current = selectedRace?.id ?? null;
  }, [selectedRace, map]);

  // Click on map background to deselect
  const handleMapClick = useCallback(() => {
    onRaceSelectRef.current(null);
  }, []);

  useEffect(() => {
    map.on('click', handleMapClick);
    return () => {
      map.off('click', handleMapClick);
    };
  }, [map, handleMapClick]);

  return null;
}
